import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import { VictoryChartCard, ChartDataItem } from './VictoryChartCard';
import { StandupCardItem } from '../../types';

// ─── Design-token colors (matching ChartsContainer palette) ──────────────────
const COLOR = {
  cyan: '#33ABDA',
  mauve: '#B05788',
  teal: '#0078A7',
  purple: '#8458CC',
  yellow: '#D6AC16',
};

// ─── Category definitions ─────────────────────────────────────────────────────
type CategoryDef = { keys: string[]; label: string; color: string };

const STATUS_CATEGORIES: CategoryDef[] = [
  { keys: ['not started', 'not_started'], label: 'Not Started', color: COLOR.cyan },
  { keys: ['incomplete', 'in_complete'], label: 'Incomplete', color: COLOR.mauve },
  { keys: ['completed', 'complete'], label: 'Completed', color: COLOR.teal },
  { keys: ['in progress', 'in_progress', 'active'], label: 'In Progress', color: COLOR.purple },
  { keys: ['saved'], label: 'Saved', color: COLOR.yellow },
];

const TYPE_CATEGORIES: CategoryDef[] = [
  { keys: ['weekly lt'], label: 'Weekly LT', color: COLOR.cyan },
  { keys: ['daily lt'], label: 'Daily LT', color: COLOR.mauve },
  { keys: ['daily flt', 'frontline'], label: 'Daily Front-line', color: COLOR.purple },
];

// ─── Helper ───────────────────────────────────────────────────────────────────

function buildChartData(
  items: StandupCardItem[],
  getValue: (item: StandupCardItem) => string,
  categories: CategoryDef[],
): ChartDataItem[] {
  const counts: number[] = categories.map(() => 0);

  items.forEach((item) => {
    // Normalize: lowercase, trim, and replace underscores with spaces for consistent matching
    const raw = getValue(item).toLowerCase().trim().replace(/_/g, ' ');
    for (let i = 0; i < categories.length; i++) {
      if (categories[i].keys.some((k) => raw.includes(k))) {
        counts[i]++;
        break;
      }
    }
    // unmatched items are silently skipped (no "Other" bucket)
  });

  // Return all categories, including those with zero counts
  return categories.map((cat, i) => ({
    x: cat.label,
    y: counts[i],
    color: cat.color,
  }));
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface StandupStatsCardProps {
  items: StandupCardItem[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export const StandupStatsCard: React.FC<StandupStatsCardProps> = ({ items }) => {
  const getStatus = (item: StandupCardItem) =>
    String((item as any).status || item.STATUS || '');

  const getType = (item: StandupCardItem) =>
    String((item as any).standup_type || item.type || item.STANDUP_TYPE || '');

  const byStatus = useMemo(
    () => buildChartData(items, getStatus, STATUS_CATEGORIES),
    [items],
  );

  const byType = useMemo(
    () => buildChartData(items, getType, TYPE_CATEGORIES),
    [items],
  );

  return (
    <View style={styles.outerCard}>
      <Text style={styles.title}>Stand-up Stats</Text>
      <View style={styles.row}>
        <VictoryChartCard title="By Status" data={byStatus} />
        <VictoryChartCard title="By Type" data={byType} />
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#005CBC',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 3,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2D2D2D',
    fontFamily: FontFamily.bold,
    lineHeight: 32,
    marginBottom: 20,
  },
  row: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 16,
    alignItems: 'stretch',
  },
});

export default StandupStatsCard;
